(() => {
  if (customElements.get("pe-shoppable-videos")) return;
  customElements.define("pe-shoppable-videos", class extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const abort = new AbortController();
      const options = { signal: abort.signal };
      this.cards = [...this.querySelectorAll(".pe-video-card")];
      this.track = this.querySelector("[data-video-track]");
      this.mobile = matchMedia("(max-width:749px)");
      this.reduced = matchMedia("(prefers-reduced-motion:reduce)");
      this.active = 0;
      this.cards.forEach(card => {
        const video = card.querySelector("video");
        const play = card.querySelector("[data-video-play]");
        const audio = card.querySelector("[data-video-audio]");
        video.muted = true;
        card.userPaused = false;
        card.restartAt = 0;
        card.restartTimer = null;
        video.loop = false;
        card.querySelector(".pe-videos__controls").hidden = false;
        const paint = () => {
          play.setAttribute("aria-label", video.paused ? play.dataset.playLabel : play.dataset.pauseLabel);
          play.setAttribute("aria-pressed", String(!video.paused));
          card.querySelector("[data-play-icon]").hidden = !video.paused;
          card.querySelector("[data-pause-icon]").hidden = video.paused;
          audio.setAttribute("aria-label", video.muted ? audio.dataset.unmuteLabel : audio.dataset.muteLabel);
          audio.setAttribute("aria-pressed", String(!video.muted));
          card.querySelector("[data-audio-on]").hidden = video.muted;
          card.querySelector("[data-audio-off]").hidden = !video.muted;
        };
        video.addEventListener("ended", () => {
          card.restartAt = Date.now() + 3000;
          if (this.canPlay(card)) this.start(card);
        }, options);
        video.addEventListener("play", paint, options);
        video.addEventListener("pause", paint, options);
        video.addEventListener("volumechange", paint, options);
        play.addEventListener("click", () => {
          if (this.mobile.matches) this.setActive(this.cards.indexOf(card));
          if (!video.paused) { card.userPaused = true; video.pause(); }
          else {
            clearTimeout(card.restartTimer); card.restartTimer = null; card.restartAt = 0;
            if (video.ended) video.currentTime = 0;
            card.userPaused = false; card.userStarted = true; this.start(card);
          }
        }, options);
        audio.addEventListener("click", () => {
          if (this.mobile.matches) this.setActive(this.cards.indexOf(card));
          video.muted = !video.muted; // User interaction only; never inferred from a click on the CTA.
          if (!video.muted) {
            this.cards.forEach(other => { if (other !== card) other.querySelector("video").muted = true; });
            if (!card.userPaused && card.inViewport) this.start(card);
          }
          paint();
        }, options);
        card.addEventListener("focusin", () => { if (this.mobile.matches) this.setActive(this.cards.indexOf(card)); }, options);
        paint();
      });
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.target.inViewport = entry.intersectionRatio >= 0.5);
        this.updatePlayback();
      }, { threshold: [0, 0.5, 1] });
      this.cards.forEach(card => observer.observe(card));
      this.querySelector("[data-video-prev]")?.addEventListener("click", () => this.navigate(-1), options);
      this.querySelector("[data-video-next]")?.addEventListener("click", () => this.navigate(1), options);
      let frame;
      this.track.addEventListener("scroll", () => {
        if (!this.mobile.matches) return;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const box = this.track.getBoundingClientRect();
          const center = box.left + box.width / 2;
          const nearest = this.cards.reduce((best, card, i) => {
            const r = card.getBoundingClientRect();
            return Math.abs(r.left + r.width / 2 - center) < best.distance ? { index: i, distance: Math.abs(r.left + r.width / 2 - center) } : best;
          }, { index: 0, distance: Infinity });
          this.setActive(nearest.index);
        });
      }, options);
      const resized = () => { this.layout(); this.updatePlayback(); };
      this.mobile.addEventListener("change", resized, options);
      this.reduced.addEventListener("change", () => this.updatePlayback(), options);
      document.addEventListener("visibilitychange", () => this.updatePlayback(), options);
      this.layout();
      this.cleanup = () => { abort.abort(); observer.disconnect(); cancelAnimationFrame(frame); this.cards.forEach(card => { clearTimeout(card.restartTimer); card.querySelector("video").pause(); }); };
    }
    disconnectedCallback() { this.cleanup?.(); this.cleanup = null; }
    load(card) {
      const video = card.querySelector("video");
      if (!video.dataset.loaded) {
        video.querySelectorAll("source[data-src]").forEach(source => source.src = source.dataset.src);
        video.dataset.loaded = "true";
        video.load();
      }
      return video;
    }
    start(card) {
      if (document.hidden) return;
      const video = this.load(card);
      if (card.restartAt) {
        const remaining = card.restartAt - Date.now();
        if (remaining > 0) {
          if (!card.restartTimer) card.restartTimer = setTimeout(() => {
            card.restartTimer = null;
            if (this.canPlay(card)) this.start(card);
          }, remaining);
          return;
        }
        card.restartAt = 0;
        video.currentTime = 0;
      }
      video.play().catch(() => {
        // Browser policy/network failure: poster and explicit play control remain.
        card.querySelector("[data-video-play]").setAttribute("aria-pressed", "false");
      });
    }
    canPlay(card) {
      const index = this.cards.indexOf(card);
      return !document.hidden && (!this.mobile.matches || index === this.active) && card.inViewport && !card.userPaused && (!this.reduced.matches || card.userStarted);
    }
    updatePlayback() {
      this.cards.forEach(card => {
        if (!this.canPlay(card)) {
          clearTimeout(card.restartTimer); card.restartTimer = null;
          card.querySelector("video").pause();
        } else this.start(card);
      });
    }
    setActive(index) {
      if (!this.mobile.matches || index === this.active) return;
      this.active = index;
      this.layout();
      this.updatePlayback();
    }
    layout() {
      const count = this.cards.length;
      this.cards.forEach((card, i) => {
        // Merchant order is permanent; controls never reposition desktop cards.
        card.style.order = 0;
        card.classList.toggle("is-active", i === (this.mobile.matches ? this.active : Math.floor(count / 2)));
      });
      const status = this.querySelector("[data-video-position]");
      if (status) status.textContent = (this.active + 1) + " / " + count;
    }
    navigate(direction) {
      if (!this.mobile.matches) return;
      const index = (this.active + direction + this.cards.length) % this.cards.length;
      this.setActive(index);
      if (this.mobile.matches) {
        const card = this.cards[index];
        this.track.scrollTo({ left: card.offsetLeft - this.track.offsetLeft, behavior: this.reduced.matches ? "instant" : "smooth" });
      }
    }
  });
})();
